import { useState, useRef, useEffect } from "react";
import { Search, Bell, Sparkles, Menu, X, CheckCheck } from "lucide-react";
import { notifications } from "../../data/mock";

interface Props {
  onMenuClick: () => void;
}

export function Topbar({ onMenuClick }: Props) {
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchValue, setSearchValue]     = useState("");
  const [notifOpen, setNotifOpen]         = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [readIds, setReadIds]             = useState<Set<string>>(new Set());
  const [botReply, setBotReply] = useState<string | null>(null);

const handleLatamClick = () => {
  setBotReply("select all suppliers with base country from south America");
};

  const notifRef     = useRef<HTMLDivElement>(null);
  const assistantRef = useRef<HTMLDivElement>(null);

  /* Close panels on outside click */
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
      // if (assistantRef.current && !assistantRef.current.contains(e.target as Node)) setAssistantOpen(false);
      if (assistantRef.current && !assistantRef.current.contains(e.target as Node)) {
        setAssistantOpen(false);
        setBotReply(null); // clear reply on outside close
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const unread = notifications.filter((n) => !n.read && !readIds.has(n.id)).length;

  const markAllRead = () => setReadIds(new Set(notifications.map((n) => n.id)));

  return (
    <header className="topbar" role="banner">
      {/* Hamburger (mobile) */}
      <button
        type="button"
        className="topbar__menu-btn"
        onClick={onMenuClick}
        aria-label="Open menu"
      >
        <Menu size={20} />
      </button>

      {/* Search bar */}
      <div
        className={`topbar-search${searchFocused ? " is-focused" : ""}`}
        role="search"
      >
        <Search size={14} className="topbar-search__icon" aria-hidden="true" />
        <input
          className="topbar-search__input"
          placeholder="Search suppliers, categories, transctions"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          aria-label="Global search"
        />
        {searchValue && (
          <button
            type="button"
            onClick={() => setSearchValue("")}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 0, display: "flex" }}
            aria-label="Clear search"
          >
            <X size={13} />
          </button>
        )}
      </div>

      <div className="topbar-actions">
        {/* Digital Assistant */}
        <div ref={assistantRef} style={{ position: "relative" }}>
          <button
            type="button"
            className="btn-digital-assistant"
            // onClick={() => setAssistantOpen((v) => !v)}
            onClick={() => {
              setAssistantOpen((v) => {
                const next = !v;
                if (!next) setBotReply(null); // clear reply when closing
                return next;
              });
            }}
            aria-expanded={assistantOpen}
            aria-haspopup="dialog"
          >
            <div className="btn-digital-assistant__avatar" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.9857 7.91627C17.9572 7.56438 17.7981 7.23589 17.5396 6.99544C17.2811 6.75499 16.9419 6.61999 16.5889 6.61702H16.0833C15.9831 6.27916 15.8402 5.95548 15.658 5.65383L16.0436 5.25626C16.2918 4.99727 16.4383 4.65735 16.4559 4.29901C16.4736 3.94067 16.3612 3.58801 16.1396 3.30588C15.7491 2.80433 15.3051 2.34679 14.8156 1.94137C14.5481 1.71357 14.2034 1.59688 13.8525 1.6153C13.5017 1.63371 13.1712 1.78583 12.929 2.04039L12.5756 2.42146C12.275 2.25598 11.9532 2.13208 11.6192 2.05314V1.49879C11.6239 1.14501 11.4983 0.801874 11.2663 0.534745C11.0343 0.267616 10.7122 0.0951687 10.3612 0.0502606C10.047 0.0170911 9.73125 0.00031414 9.41528 0V3.27738C10.8719 3.33057 12.251 3.94661 13.2627 4.99589C14.2744 6.04517 14.8396 7.44591 14.8396 8.90346C14.8396 10.361 14.2744 11.7617 13.2627 12.811C12.251 13.8603 10.8719 14.4763 9.41528 14.5295V17.8062C9.73125 17.8059 10.047 17.7892 10.3612 17.7559C10.7124 17.711 11.0348 17.5383 11.2668 17.2709C11.4988 17.0034 11.6243 16.6599 11.6192 16.3059V15.7808C11.9462 15.673 12.2589 15.5256 12.5501 15.3419L12.9432 15.7395C13.1853 15.9942 13.5158 16.1464 13.8667 16.165C14.2175 16.1836 14.5622 16.067 14.8298 15.8393C15.3196 15.4339 15.7638 14.9763 16.1546 14.4748C16.3759 14.1938 16.4881 13.8421 16.4703 13.4848C16.4525 13.1276 16.306 12.7888 16.0578 12.5312L15.6895 12.1666C15.8546 11.8567 15.9763 11.5255 16.0511 11.1824H16.6047C16.9562 11.1792 17.294 11.0451 17.552 10.8062C17.81 10.5674 17.9697 10.2409 17.9999 9.89064" fill="#E40958"/>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M8.35465 3.16947V0.078125C6.90186 0.124416 5.48507 0.541607 4.23903 1.29002C2.99299 2.03843 1.95913 3.09318 1.23579 4.35394C0.490569 5.63745 0.0690974 7.08312 0.00780004 8.56602C-0.0534973 10.0489 0.247224 11.5244 0.883974 12.8651L0.253854 16.8086C0.239541 16.8963 0.244758 16.9861 0.269136 17.0715C0.293513 17.157 0.33645 17.236 0.394881 17.3029C0.451829 17.3684 0.523211 17.4198 0.6034 17.453C0.683589 17.4862 0.770393 17.5003 0.856968 17.4942L5.34808 17.1604C6.30172 17.5613 7.32199 17.7801 8.35616 17.8055V14.7149C6.8941 14.6135 5.52498 13.9611 4.52518 12.8896C3.52537 11.818 2.96934 10.407 2.96934 8.94145C2.96934 7.47588 3.52537 6.06489 4.52518 4.99332C5.52498 3.92175 6.8941 3.2694 8.35616 3.16797L8.35465 3.16947Z" fill="#F5F5F5"/>
                <path d="M6.04866 10.069C6.59843 10.069 7.0441 9.62333 7.0441 9.07357C7.0441 8.5238 6.59843 8.07812 6.04866 8.07812C5.4989 8.07812 5.05322 8.5238 5.05322 9.07357C5.05322 9.62333 5.4989 10.069 6.04866 10.069Z" fill="#F5F5F5"/>
                <path d="M8.79427 10.069C9.34404 10.069 9.78971 9.62333 9.78971 9.07357C9.78971 8.5238 9.34404 8.07812 8.79427 8.07812C8.2445 8.07812 7.79883 8.5238 7.79883 9.07357C7.79883 9.62333 8.2445 10.069 8.79427 10.069Z" fill="#F5F5F5"/>
                <path d="M11.5396 10.069C12.0894 10.069 12.5351 9.62333 12.5351 9.07357C12.5351 8.5238 12.0894 8.07812 11.5396 8.07812C10.9899 8.07812 10.5442 8.5238 10.5442 9.07357C10.5442 9.62333 10.9899 10.069 11.5396 10.069Z" fill="#F5F5F5"/>
              </svg>
            </div>
            <div className="btn-digital-assistant__label">
              Digital Assistant <span>by GenAI</span>
            </div>
            
          </button>

          {assistantOpen && (
            <div
              style={{
                position: "absolute", top: "calc(100% + 8px)", right: 0,
                width: 320, background: "#fff", borderRadius: 12,
                border: "1px solid var(--border)", boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                padding: 0, zIndex: 300, overflow: "hidden",
              }}
              role="dialog"
              aria-label="Digital Assistant"
            >
              <div style={{ background: "linear-gradient(135deg, #6b3fa0, #983683)", padding: "14px 16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Sparkles size={14} color="#fff" />
                  </div>
                  <div>
                    <div style={{ color: "#fff", fontSize: 13, fontWeight: 700 }}>SpendIQ Assistant</div>
                    <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 11 }}>AI-powered procurement advisor</div>
                  </div>
                </div>
              </div>
              <div style={{ padding: "12px 16px" }}>
                <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 8 }}>Ask me anything:</div>
                {["What are my top savings opportunities?", "Which contracts expire this month?", "Show LATAM supplier risk summary"].map((q) => (
                  <button
                    key={q}
                    type="button"
                    style={{
                      display: "block", width: "100%", textAlign: "left",
                      background: "var(--surface-page)", border: "1px solid var(--border)",
                      borderRadius: 8, padding: "8px 12px", fontSize: 12,
                      color: "var(--text-secondary)", cursor: "pointer", marginBottom: 6,
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#ede9f7")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "var(--surface-page)")}
                    onClick={q === "Show LATAM supplier risk summary" ? handleLatamClick : undefined}
                  >
                    {q}
                  </button>
                ))}
                
                
                {botReply && (
                  <div
                    style={{
                      marginTop: 12,
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 8,
                    }}
                  >
                    {/* Bot avatar */}
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        flexShrink: 0,
                        background: "linear-gradient(135deg, #6b3fa0, #983683)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      aria-hidden="true"
                    >
                      <Sparkles size={13} color="#fff" />
                    </div>

                    {/* Message bubble */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: "var(--text-muted)",
                          marginBottom: 4,
                          letterSpacing: "0.02em",
                        }}
                      >
                        SpendIQ Assistant
                      </div>
                      <div
                        style={{
                          background: "linear-gradient(135deg, #f6f3fb 0%, #f0ebf8 100%)",
                          border: "1px solid rgba(107, 63, 160, 0.12)",
                          borderRadius: "4px 12px 12px 12px",
                          padding: "10px 12px",
                          fontSize: 12,
                          lineHeight: 1.45,
                          color: "var(--text-primary)",
                          boxShadow: "0 1px 2px rgba(107, 63, 160, 0.06)",
                        }}
                      >
                        {botReply}
                      </div>
                    </div>
                  </div>
                )}
                <input
                  placeholder="Type your question..."
                  style={{
                    width: "100%", marginTop: 4, padding: "8px 12px",
                    border: "1px solid var(--border)", borderRadius: 8,
                    fontSize: 12, outline: "none", fontFamily: "inherit",
                  }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = "#6b3fa0")}
                  onBlur={(e) => (e.currentTarget.style.borderColor = "var(--border)")}
                />
              </div>
            </div>
          )}
        </div>

        {/* Notifications */}
        <div ref={notifRef} style={{ position: "relative" }}>
          <button
            type="button"
            className="topbar-notification"
            onClick={() => setNotifOpen((v) => !v)}
            aria-label={`Notifications${unread > 0 ? `, ${unread} unread` : ""}`}
            aria-expanded={notifOpen}
            aria-haspopup="dialog"
          >
            <Bell size={16} />
            {unread > 0 && <span className="topbar-notification__badge" aria-hidden="true" />}
          </button>

          {notifOpen && (
            <div
              style={{
                position: "absolute", top: "calc(100% + 8px)", right: 0,
                width: 320, background: "#fff", borderRadius: 12,
                border: "1px solid var(--border)", boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                zIndex: 300, overflow: "hidden",
              }}
              role="dialog"
              aria-label="Notifications"
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>Notifications</span>
                <button
                  type="button"
                  onClick={markAllRead}
                  style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--brand-blue)" }}
                >
                  <CheckCheck size={13} /> Mark all read
                </button>
              </div>
              <div>
                {notifications.map((n) => {
                  const isRead = n.read || readIds.has(n.id);
                  const colors: Record<string, string> = { critical: "#ef4444", warning: "#f59e0b", info: "#6b3fa0" };
                  return (
                    <div
                      key={n.id}
                      style={{
                        display: "flex", alignItems: "flex-start", gap: 10,
                        padding: "12px 16px", borderBottom: "1px solid var(--border-light)",
                        background: isRead ? "#fff" : "rgba(107,63,160,0.04)",
                        cursor: "pointer",
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-page)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = isRead ? "#fff" : "rgba(107,63,160,0.04)")}
                    >
                      <span style={{
                        width: 8, height: 8, borderRadius: "50%", flexShrink: 0, marginTop: 4,
                        background: isRead ? "var(--border)" : colors[n.severity] || "#6b3fa0",
                      }} />
                      <div>
                        <div style={{ fontSize: 12, fontWeight: isRead ? 400 : 600, color: "var(--text-primary)", marginBottom: 2 }}>{n.title}</div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{n.time}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ padding: "10px 16px", textAlign: "center" }}>
                <button type="button" style={{ fontSize: 12, color: "var(--brand-blue)", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>
                  View all notifications
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
